import { bucketLabel, bucketPctText, selectLead } from "../lib/forecast/multiway.js";
import { isOtherOutcome } from "../lib/forecast/answerSummary.js";
import { pctColumnWidth, TIMELINE_TOKENS } from "../lib/timeline/multiwayMarker.js";
import { ui } from "../lib/telemetry/instrument.js";
import { rt } from "../lib/report/typeScale.js";
import { fmtDeltaPts } from "../lib/timeline/hoverPreview.js";
import { prefersReducedMotion } from "../lib/util/reducedMotion.js";
import { IconCheck } from "./ui/Icons.jsx";
import { Chip } from "./ui/Primitives.jsx";

/**
 * The distribution readout: one row per outcome, as three aligned columns — BAR, PERCENT,
 * LABEL — with the row the answer names set in strong type.
 *
 * Works for any bucket count, from a yes/no pair to the maximum a question can carry. Every
 * column width is fixed, so the emphasised row grows in TYPE only (never in geometry) and bar
 * left edges, percent right edges and label left edges line up down the whole block.
 *
 * ORDER AND EMPHASIS (PROTOTYPE.md "Numbers"):
 *  • yes/no — Yes then No; the larger side is the emphasised row (the lead `selectLead` picks,
 *    as for any distribution) and the other side is muted.
 *  • multi-outcome — named outcomes in the order given (by probability for a nominal space,
 *    authored order for a scale), every catch-all ("Other (not listed)", "Any other …") LAST,
 *    muted, with a hatched bar. The emphasised row is the lead `selectLead` picks: the top NAMED
 *    outcome unless the catch-all outweighs all of them together.
 *
 * Two variants share the look; positioning and sizing are separate axes:
 *  • "timeline" (default) — absolute-positioned off the forecast line at `markerX`/`spineY`;
 *    deliberately compact now that the forecast header carries the hero number.
 *  • "report" — static flow, sized for the report headline's right column.
 *
 * RESOLUTION. `resolvedIndex` names the outcome that occurred (an index into `buckets` as
 * passed). The bars stay whatever the caller passes — on the timeline, Sayer's own final
 * forecast (lib/timeline/resolvedOutcome.js `withForecastBars`) — and the row that happened is
 * outlined in gold with a check while the others are muted, so the block reads as forecast vs
 * outcome. A `badge` with no `resolvedIndex` ("Tied") renders as a line under the bars.
 *
 * PREVIEW. `preview` (lib/timeline/hoverPreview.js `hoverPreviewOf`, timeline variant only) is
 * the forecast if a hovered trigger happens: each bar eases to its conditional width — the part
 * gained in the raise colour, the part lost left as a faint hatched outline in the lower colour —
 * each percent shows its new value with a signed delta, a new leader takes the emphasis with a
 * "New leader" chip, and a caption names the trigger. The block's footprint never changes: the
 * deltas, the shifted labels and the caption ride on absolute positions and transforms.
 *
 * Pure presentational — geometry, data, and the open-report handler are passed in.
 */

const WIN_WEIGHT = 600;
// Room the labels slide right to make for the delta column while previewing, in px.
const DELTA_SHIFT = 30;
const PREVIEW_MS = 180;

export default function MultiwayMarker({
  buckets,
  markerX,
  spineY,
  C,
  onOpen,
  variant = "timeline",
  kind = null,
  resolvedIndex = null,
  badge = null,
  tourSpotlight = undefined,
  onHoverEnter,
  onHoverMove,
  onHoverLeave,
  onBinHover,
  preview = null,
  "data-ui": uiId,
  "data-ui-key": uiKey,
}) {
  const isReport = variant === "report";
  const isFloating = variant === "timeline";
  const t = isReport
    ? {
        rowH: 19,
        barTrack: 94,
        barH: 8,
        pctSize: 11,
        winPctSize: 15,
        labelSize: 12,
        winLabelSize: 13,
        pctGap: 3,
        labelGap: 11,
        railW: 2,
        padL: 7,
        padR: 11,
        badgeSize: 11,
      }
    : // The timeline geometry (row height, track, column gaps) is unchanged so the lane's
      // footprint math in lib/timeline/multiwayMarker.js still holds; only the type is smaller.
      {
        ...TIMELINE_TOKENS,
        barH: 8,
        pctSize: 12,
        winPctSize: 15,
        labelSize: 12,
        winLabelSize: 13,
        badgeSize: 11,
      };

  // The accent marks only the answer; the other named rows are neutral, the catch-all is muted
  // and hatched. `color-mix` (not a hex-alpha suffix) because the tokens are `var()` references.
  const LEAD_FILL = C.accent;
  const NAMED_FILL = `color-mix(in srgb, ${C.grayL} 50%, transparent)`;
  const HATCH = `repeating-linear-gradient(135deg, ${C.gray} 0 1.5px, transparent 1.5px 4.5px)`;
  const TRACK_TINT = `color-mix(in srgb, ${C.grayL} 14%, transparent)`;
  // Opaque under the tint, so the dashed spine passing behind the marker never shows through.
  const TRACK_GROUND = C.future;
  const SETTLED_COLOR = C.gold || "#F7CE46";

  // Catch-alls last; the resolved row is followed to its new position by identity.
  const given = buckets || [];
  const resolvedBucket = typeof resolvedIndex === "number" ? given[resolvedIndex] : undefined;
  const isNumeric = kind === "numeric";
  const rows = isNumeric
    ? given
    : [...given.filter((b) => !isOtherOutcome(b)), ...given.filter((b) => isOtherOutcome(b))];
  const rowResolvedIndex = resolvedBucket === undefined ? null : rows.indexOf(resolvedBucket);

  const isBinary =
    kind === "binary" || (rows.length === 2 && rows[0]?.label === "Yes" && rows[1]?.label === "No");
  const maxP = Math.max(...rows.map((b) => b.p || 0), 0.001);
  const { resolvedRow, lead } = selectLead(rows, rowResolvedIndex);
  const tied = badge === "Tied" ? rows.filter((b) => Math.abs((b.p || 0) - maxP) < 1e-9) : [];
  const isTie = tied.length > 1;
  const settledNoWinner = badge === "Resolved" && !resolvedRow;
  const tip = rows.map((b) => `${bucketLabel(b)}: ${bucketPctText(b.p)}`).join("\n");

  const pctColW = pctColumnWidth(t);
  // The hover preview, when it lines up with these buckets (values are aligned with `buckets`
  // as passed; rows above are the same objects reordered).
  const pv =
    isFloating && preview && Array.isArray(preview.values) && preview.values.length === given.length
      ? preview
      : null;
  const pOf = (b) => (pv ? pv.values[given.indexOf(b)] : b.p);
  // While previewing, the emphasis moves to the side the trigger would leave in the lead. A yes/no
  // preview at exactly 50% names no leader (`leadAfter` null) and takes the first row, as
  // `selectLead` breaks a live tie.
  const shownLead = !pv
    ? lead
    : pv.leadAfter != null
      ? given[pv.leadAfter]
      : isBinary
        ? rows[0]
        : lead;
  const newLead = pv?.leaderChanged && pv.leadAfter != null ? given[pv.leadAfter] : null;
  const ease = (prop) => (prefersReducedMotion() ? "none" : `${prop} ${PREVIEW_MS}ms ease-out`);
  // The share a trigger would take away: a faint hatched outline in the lower colour.
  const LOST_FILL = `repeating-linear-gradient(135deg, color-mix(in srgb, ${C.redTeam} 45%, transparent) 0 1.5px, transparent 1.5px 4px)`;
  const LOST_LINE = `color-mix(in srgb, ${C.redTeam} 75%, transparent)`;
  const isWinRow = (b) => (isTie ? tied.includes(b) : b === shownLead);
  const showBadgeLine = !!badge && !resolvedRow && !settledNoWinner;

  const wrapperStyle = isFloating
    ? {
        position: "absolute",
        // Always to the right of the forecast line: the answer is the line's output.
        left: markerX + 8,
        // Centred on the spine by transform, so adding a row or the badge line needs no
        // height bookkeeping.
        top: spineY,
        transform: "translateY(-50%)",
        zIndex: 30,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
      }
    : {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        cursor: onOpen ? "pointer" : "default",
      };

  return (
    <div
      {...ui(uiId, uiKey)}
      onClick={onOpen || undefined}
      title={isFloating && !onHoverEnter ? tip : undefined}
      onMouseEnter={onHoverEnter}
      onMouseMove={onHoverMove}
      onMouseLeave={onHoverLeave}
      // The onboarding tour spotlights the resolution marker by this attribute.
      data-tour-spotlight={tourSpotlight}
      data-clears-question={isFloating ? "" : undefined}
      data-answer-kind={isBinary ? "binary" : "multi"}
      data-previewing={pv ? "" : undefined}
      style={wrapperStyle}
    >
      {pv && (
        <div
          data-preview-caption=""
          style={{
            position: "absolute",
            left: t.padL + t.railW,
            bottom: "100%",
            marginBottom: 6,
            fontSize: 12,
            lineHeight: "16px",
            color: C.gray,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            animation: prefersReducedMotion() ? "none" : `simpleFadeIn ${PREVIEW_MS}ms ease-out`,
          }}
        >
          {pv.caption}
        </div>
      )}
      {rows.map((b, i) => {
        const isWin = isWinRow(b);
        const isResolved = resolvedRow ? b === resolvedRow : false;
        const settled = isResolved || (settledNoWinner && b === lead);
        const isTagRow = isTie ? b === tied[0] : b === lead;
        const other = !isNumeric && isOtherOutcome(b);
        // Muted: the catch-all, the smaller side of a live yes/no question, and once a question
        // settles, every outcome that did not happen.
        const muted = !isWin && !settled && (other || isBinary || !!resolvedRow);
        const textColor = settled ? SETTLED_COLOR : isWin ? C.white : muted ? C.gray : C.grayL;
        // Absolute scale: the fill IS the probability; a ruled-out outcome reads as empty.
        const fillOf = (p) => ((p || 0) <= 0 ? 0 : Math.max(2.5, (p || 0) * 100));
        const fill = fillOf(b.p);
        const pNext = pOf(b);
        const fillNext = pv ? fillOf(pNext) : fill;
        const gained = fillNext > fill;
        const lost = fillNext < fill;
        const delta = pv ? fmtDeltaPts(b.p, pNext) : "";
        // A one-point move keeps its sign but not the colour, so the moves that matter stand out.
        const deltaColor =
          Math.abs(Math.round(((pNext || 0) - (b.p || 0)) * 100)) < 2
            ? C.gray
            : pNext > b.p
              ? C.blueTeam
              : C.redTeam;
        const isNewLead = !!newLead && b === newLead;
        const barFill = other
          ? HATCH
          : isWin || settled
            ? LEAD_FILL
            : isBinary || resolvedRow
              ? `color-mix(in srgb, ${C.gray} 55%, transparent)`
              : NAMED_FILL;
        return (
          <div
            key={i}
            data-answer-row={isWin ? "lead" : other ? "other" : "named"}
            // Per-row hover tells the timeline which outcome to trace on the probability plot.
            onMouseEnter={onBinHover ? () => onBinHover(b.label ?? null) : undefined}
            onMouseLeave={onBinHover ? () => onBinHover(null) : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              ...(isReport ? { minHeight: t.rowH } : { height: t.rowH }),
              borderLeft: `${t.railW}px solid transparent`,
              paddingLeft: t.padL,
              paddingRight: t.padR,
              // The outcome that happened, outlined in gold. An inset outline
              // so the row's footprint (and the lane's marker geometry) never changes.
              ...(isResolved
                ? {
                    outline: `1px solid ${SETTLED_COLOR}`,
                    outlineOffset: -1,
                    borderRadius: 6,
                    background: `color-mix(in srgb, ${SETTLED_COLOR} 9%, transparent)`,
                  }
                : {}),
            }}
          >
            <div
              style={{
                position: "relative",
                width: t.barTrack,
                height: t.barH,
                backgroundColor: TRACK_GROUND,
                backgroundImage: `linear-gradient(${TRACK_TINT}, ${TRACK_TINT})`,
                borderRadius: 2,
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {isFloating && (
                // What the trigger would take away: the span between the new end and the current
                // one, opening in step with the fill as it eases back. Only that span — the
                // named and catch-all fills are translucent, so a ghost under them would tint
                // the whole bar.
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: `${lost ? fillNext : fill}%`,
                    top: 0,
                    width: `${lost ? fill - fillNext : 0}%`,
                    height: "100%",
                    background: LOST_FILL,
                    boxShadow: `inset 0 0 0 1px ${LOST_LINE}`,
                    borderRadius: "0 2px 2px 0",
                    transition: prefersReducedMotion()
                      ? "none"
                      : `left ${PREVIEW_MS}ms ease-out, width ${PREVIEW_MS}ms ease-out`,
                  }}
                />
              )}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: `${Math.min(fill, fillNext)}%`,
                  height: "100%",
                  background: barFill,
                  borderRadius: gained || lost ? "2px 0 0 2px" : 2,
                  transition: ease("width"),
                  ...(other
                    ? {
                        boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${C.gray} 50%, transparent)`,
                      }
                    : {}),
                }}
              />
              {isFloating && (
                // What the trigger would add, grown out from the current bar's end.
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: `${fill}%`,
                    top: 0,
                    width: `${gained ? fillNext - fill : 0}%`,
                    height: "100%",
                    background: C.blueTeam,
                    borderRadius: "0 2px 2px 0",
                    transition: ease("width"),
                  }}
                />
              )}
            </div>
            <div
              className="sy-num"
              style={{
                position: "relative",
                width: pctColW,
                marginLeft: t.pctGap,
                flexShrink: 0,
                textAlign: "right",
                fontSize: rt(isWin ? t.winPctSize : t.pctSize),
                fontWeight: isWin ? WIN_WEIGHT : 400,
                lineHeight: 1,
                color: textColor,
              }}
            >
              {bucketPctText(pNext)}
              {delta && (
                <span
                  data-preview-delta=""
                  className="sy-num"
                  style={{
                    position: "absolute",
                    left: "100%",
                    top: "50%",
                    transform: "translateY(-50%)",
                    marginLeft: 6,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    color: deltaColor,
                    pointerEvents: "none",
                  }}
                >
                  {delta}
                </span>
              )}
            </div>
            <div
              style={{
                marginLeft: t.labelGap,
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: rt(isWin ? t.winLabelSize : t.labelSize),
                fontWeight: isWin ? WIN_WEIGHT : 400,
                letterSpacing: 0,
                color: textColor,
                ...(isReport
                  ? {
                      minWidth: 0,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      lineHeight: 1.25,
                    }
                  : { lineHeight: 1, whiteSpace: "nowrap" }),
                ...(isFloating
                  ? {
                      transform: pv ? `translateX(${DELTA_SHIFT}px)` : "none",
                      transition: ease("transform"),
                    }
                  : {}),
              }}
            >
              {bucketLabel(b)}
              {isNewLead && (
                <Chip
                  data-preview-new-leader=""
                  style={{
                    marginLeft: 6,
                    padding: "3px 7px",
                    fontWeight: 500,
                    color: C.white,
                    borderColor: `color-mix(in srgb, ${C.accent} 60%, transparent)`,
                  }}
                >
                  New leader
                </Chip>
              )}
              {settled && isTagRow && (
                <span
                  // The check alone marks the outcome that happened; the header states the
                  // verdict in words. A verdict naming no row keeps its word.
                  role={isResolved ? "img" : undefined}
                  aria-label={isResolved ? "Happened" : undefined}
                  title={isResolved ? "Happened" : undefined}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    fontSize: rt(t.badgeSize),
                    fontWeight: 500,
                    marginLeft: 4,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  <IconCheck width={isResolved ? 13 : 11} height={isResolved ? 13 : 11} />
                  {!isResolved && (badge || "Resolved")}
                </span>
              )}
            </div>
          </div>
        );
      })}
      {showBadgeLine && (
        <div
          style={{
            marginTop: 4,
            paddingLeft: t.padL + t.railW,
            fontSize: rt(t.badgeSize),
            color: C.grayL,
            whiteSpace: "nowrap",
          }}
        >
          {badge}
        </div>
      )}
    </div>
  );
}
